/**
 * Resource loader utility for validation translations
 * Provides functions to load and manage translations from resources/modules JSON files
 */

import { IResourcesModule, ITranslationContext } from './types';

/**
 * Default configuration for resource loader
 */
const DEFAULT_CONFIG = {
    defaultLocale: 'en-us',
    fallbackLocale: 'en-us',
    supportedLocales: ['en-us', 'it', 'fr', 'de', 'en-GB', 'en-AU', 'en-HK', 'en-SG', 'zh-Hant'],
    resourceBasePath: '../../../resources/modules'
};

/**
 * Cache for loaded resources to avoid repeated imports
 */
const resourceCache = new Map<string, IResourcesModule>();

/**
 * Loads resource module for a specific locale
 * @param locale - The locale to load resources for
 * @returns Promise resolving to the resource module
 */
export async function loadResourceModule(locale: string): Promise<IResourcesModule> {
    // Check cache first
    if (resourceCache.has(locale)) {
        return resourceCache.get(locale)!;
    }

    try {
        // Normalize locale (handle case variations)
        const normalizedLocale = normalizeLocale(locale);

        // Dynamic import of the resource module
        const resourceModule = await import(`../../../resources/modules/${normalizedLocale}.json`);
        const resources: IResourcesModule = resourceModule.default || resourceModule;

        // Cache the loaded resources
        resourceCache.set(locale, resources);
        resourceCache.set(normalizedLocale, resources);

        return resources;
    } catch (error) {
        console.warn(`Failed to load resources for locale ${locale}:`, error);

        // Fallback to default locale if not already trying it
        if (locale !== DEFAULT_CONFIG.fallbackLocale) {
            return loadResourceModule(DEFAULT_CONFIG.fallbackLocale);
        }

        // Return empty resource module as final fallback
        return {};
    }
}

/**
 * Creates a translation context with loaded resources
 * @param locale - The locale for the context
 * @param customResources - Optional custom resources to use instead of loading from files
 * @returns Promise resolving to translation context
 */
export async function createTranslationContext(locale: string, customResources?: IResourcesModule): Promise<ITranslationContext> {
    const resources = customResources || (await loadResourceModule(locale));

    return {
        locale: normalizeLocale(locale),
        resources,
        resourcesModule: resources
    };
}

/**
 * Gets a translated message from resources
 * @param key - The resource key to look up
 * @param context - Translation context
 * @param fallbackMessage - Fallback message if key not found
 * @returns The translated message or fallback
 */
export function getTranslatedMessage(key: string, context: ITranslationContext, fallbackMessage: string): string {
    const resourceEntry = context.resources[key];

    if (resourceEntry) {
        if (typeof resourceEntry === 'object' && resourceEntry.value) {
            return resourceEntry.value;
        }

        if (typeof resourceEntry === 'string') {
            return resourceEntry;
        }
    }

    try {
        if (typeof process !== 'undefined' && (process as any).env && (process as any).env.NODE_ENV === 'development') {
            console.warn(`Translation missing for key "${key}" in locale "${context.locale}"`);
        }
    } catch (e) {
        // ignore - running in an environment without process
    }

    return fallbackMessage;
}

/**
 * Gets multiple translated messages at once
 * @param keys - Array of resource keys to look up
 * @param context - Translation context
 * @param fallbackMessages - Fallback messages for each key
 * @returns Object with translated messages
 */
export function getTranslatedMessages(
    keys: string[],
    context: ITranslationContext,
    fallbackMessages: { [key: string]: string }
): { [key: string]: string } {
    const result: { [key: string]: string } = {};

    keys.forEach(key => {
        result[key] = getTranslatedMessage(key, context, fallbackMessages[key] || key);
    });

    return result;
}

/**
 * Normalizes locale string to match file naming convention
 * @param locale - Raw locale string
 * @returns Normalized locale string
 */
function normalizeLocale(locale: string): string {
    if (!locale) {
        return DEFAULT_CONFIG.defaultLocale;
    }

    // Handle common variations
    const normalized = locale.toLowerCase();

    // Map common variations to supported locales
    const localeMap: { [key: string]: string } = {
        en: 'en-us',
        english: 'en-us',
        it: 'it',
        italian: 'it',
        fr: 'fr',
        french: 'fr',
        de: 'de',
        german: 'de',
        'en-gb': 'en-GB',
        'en-au': 'en-AU',
        'en-hk': 'en-HK',
        'en-sg': 'en-SG',
        'zh-hant': 'zh-Hant',
        chinese: 'zh-Hant'
    };

    return localeMap[normalized] || locale;
}

/**
 * Checks if a locale is supported
 * @param locale - Locale to check
 * @returns True if locale is supported
 */
export function isSupportedLocale(locale: string): boolean {
    const normalized = normalizeLocale(locale);
    return DEFAULT_CONFIG.supportedLocales.includes(normalized);
}

/**
 * Gets the list of supported locales
 * @returns Array of supported locale strings
 */
export function getSupportedLocales(): string[] {
    return [...DEFAULT_CONFIG.supportedLocales];
}

/**
 * Clears the resource cache (useful for testing or hot reloading)
 */
export function clearResourceCache(): void {
    resourceCache.clear();
}

/**
 * Preloads resources for multiple locales
 * @param locales - Array of locales to preload
 * @returns Promise that resolves when all resources are loaded
 */
export async function preloadResources(locales: string[]): Promise<void> {
    const loadPromises = locales.map(locale => loadResourceModule(locale));
    await Promise.all(loadPromises);
}
